import { render, h } from "preact";
import { useRef, useEffect, useState, useCallback } from "preact/hooks";
import "./preact-map-itinerary.component.scss";
import {
    Layer,
    Map,
    Marker,
    NavigationControl,
    Popup,
    Source,
} from "react-map-gl";
import { quietenMapLabels } from "../../../../../assets/js/lib/content/map-style";
import {
    MapMarker,
    REVISIT_OFFSET,
    formatDaysLabel,
    getStopZIndex,
    toStops,
} from "../../../../_parts/map-marker/map-marker.jsx";

// Gentle ease so camera moves glide instead of snapping.
const easeInOutCubic = (t) =>
    t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

// Afstand van de popup tot het midden van zijn marker.
const POPUP_GAP = 22;

// Wat er in de kaart naast de dagenlijst van de popup nog in beeld moet blijven: de kop "Dag x
// t/m y", de padding van de popup, de pijl en de marker eronder, plus wat lucht tot de rand.
const POPUP_CHROME = 110;

/**
 * @typedef {Object} Options
 * @property {number} focusZoomLevel
 */

/**
 * The configuration config for the map component
 * @typedef {Object} MapConfig
 * @property {import('react-map-gl').MapProps} mapOptions Any mapbox options
 * @property {Options} options
 * @property {Point[]} points
 */

function PreactMapItinerary() {
    /** @type {import('preact/hooks').Ref<import('react-map-gl').MapRef>} */
    const mapRef = useRef();
    const [activeStopId, setActiveStopId] = useState(null);

    /** @type {MapConfig} */
    const config = window.__map_itinerary_config;

    const points = config.points || [];
    // De stops hangen alleen van de route af, niet van de zoom, dus ze staan er meteen.
    const stops = toStops(points);

    // De aangeklikte stop, waarvan de popup de dagen laat zien.
    const activeStop = stops.find((stop) => stop.id === activeStopId) || null;

    /** @type {import('mapbox-gl').GeoJSONSourceOptions['data']} */
    const routeData = {
        type: "Feature",
        geometry: {
            type: "LineString",
            coordinates: getCoordinates(points),
        },
    };

    /** @param {Point[]} points */
    function getCoordinates(points) {
        return points.map((point) => [point.longitude, point.latitude]);
    }

    /**
     * @param {Point[]} points
     * @return {import('mapbox-gl').LngLatBoundsLike}
     */
    function getBounds(points) {
        if (points.length <= 0) {
            return [
                [-90, 90],
                [90, -90],
            ];
        }

        const coordinates = getCoordinates(points);

        const longtitudes = coordinates.map((point) => point[0]);
        const latitudes = coordinates.map((point) => point[1]);

        return [
            [Math.min(...longtitudes), Math.min(...latitudes)],
            [Math.max(...longtitudes), Math.max(...latitudes)],
        ];
    }

    /**
     * Sluit de popup en breng de hele route weer in beeld.
     */
    function closePoint() {
        setActiveStopId(null);
        fitBounds(getBounds(points));
    }

    /**
     * Open een stop. De kaart schuift pas als de popup er staat (zie het effect hieronder), want
     * dan is bekend hoe hoog hij is.
     *
     * @param {Stop} stop
     */
    function openStop(stop) {
        setActiveStopId(stop.id);
    }

    /**
     * Zet de open stop zo neer dat marker én popup samen in het midden van de kaart staan. Alleen
     * de stop centreren liet een popup met veel dagen boven de kaart uitsteken: hij hangt boven
     * zijn marker, dus hij had maar de halve kaarthoogte.
     */
    useEffect(() => {
        if (!activeStop || !mapRef.current) {
            return;
        }

        const frame = requestAnimationFrame(() => {
            const popup = mapRef.current
                ?.getContainer()
                .querySelector(".mapboxgl-popup-content");
            const height = popup ? popup.offsetHeight : 0;

            mapRef.current?.easeTo({
                center: [activeStop.longitude, activeStop.latitude],
                offset: [0, Math.round((height + POPUP_GAP) / 2)],
                duration: 500,
                essential: true,
                easing: easeInOutCubic,
            });
        });

        return () => cancelAnimationFrame(frame);
    }, [activeStopId]);

    /**
     * De dagenlijst in de popup mag niet hoger worden dan de kaart toelaat; wat niet past scrolt.
     * De maat hangt aan de kaart zelf, dus hij volgt ook een smallere (en dus lagere) kaart.
     */
    function updatePopupMaxHeight() {
        const container = mapRef.current?.getContainer();

        if (!container) {
            return;
        }

        container.style.setProperty(
            "--sb-map-popup-days-max-height",
            `${Math.max(80, container.offsetHeight - POPUP_CHROME)}px`
        );
    }

    function getResponsivePadding() {
        const container = mapRef.current.getContainer();
        const minDimension = Math.min(container.offsetWidth, container.offsetHeight);

        return Math.floor(minDimension * 0.16);
    }

    /** @param {import('mapbox-gl').LngLatBoundsLike} bounds */
    function fitBounds(bounds) {
        if (!mapRef.current) return;

        const options = {
            ...config.mapOptions?.initialViewState?.fitBoundsOptions,
            padding: getResponsivePadding(),
            duration: 800,
            essential: true,
            easing: easeInOutCubic,
        };

        mapRef.current.fitBounds(bounds, options);
    }

    const onMapLoad = useCallback((event) => {
        // Declutter the basemap: keep only country/island/capital labels.
        quietenMapLabels(event?.target);
        fitBounds(getBounds(points), { duration: 0 });
        updatePopupMaxHeight();
    }, []);

    // Een klik naast de kaart sluit de popup ook. Klikken ín de kaart doet Mapbox zelf via
    // `closeOnClick`; zonder dit bleef de popup open staan terwijl de bezoeker verder las.
    useEffect(() => {
        if (activeStopId === null) {
            return;
        }

        function onDocumentClick(event) {
            const container = mapRef.current?.getContainer();

            if (container && !container.contains(event.target)) {
                closePoint();
            }
        }

        document.addEventListener("click", onDocumentClick);

        return () => document.removeEventListener("click", onDocumentClick);
    }, [activeStopId]);

    // Escape sluit de popup, zoals bij elke overlay. Klikken naast de marker doet Mapbox zelf via
    // `closeOnClick`, en het kruisje zit in de popup.
    useEffect(() => {
        function onKeyDown(event) {
            if (event.key === "Escape" && activeStopId !== null) {
                closePoint();
            }
        }

        window.addEventListener("keydown", onKeyDown);

        return () => window.removeEventListener("keydown", onKeyDown);
    }, [activeStopId, points]);

    return (
        <Map
            ref={mapRef}
            {...config.mapOptions}
            onLoad={onMapLoad}
            onResize={() => {
                updatePopupMaxHeight();

                if (activeStopId === null) {
                    fitBounds(getBounds(points));
                }
            }}
            initialViewState={{
                bounds: getBounds(points),
                ...config.mapOptions?.initialViewState,
            }}
            locale={{
                "ScrollZoomBlocker.CtrlMessage":
                    "Gebruik ctrl + scroll om in te zoomen",
                "ScrollZoomBlocker.CmdMessage":
                    "Gebruik ⌘ + scroll om in te zoomen",
                "TouchPanBlocker.Message":
                    "Gebruik twee vingers om de kaart te verplaatsen",
            }}
        >
            {/* In- en uitzoomen met knoppen, net als de kaart op de filterpagina. */}
            <NavigationControl showCompass={false} />

            <Source id="route" type="geojson" data={routeData}>
                <Layer
                    id="route"
                    type="line"
                    paint={{
                        "line-color": "#083941",
                        "line-width": 3,
                        "line-opacity": 0.4,
                    }}
                />
            </Source>

            {stops.map((stop) => {
                const isActive = activeStop?.id === stop.id;

                return (
                    <Marker
                        key={stop.id}
                        longitude={stop.longitude}
                        latitude={stop.latitude}
                        anchor="center"
                        offset={[stop.revisit * REVISIT_OFFSET, 0]}
                        onClick={(event) => {
                            // Anders vuurt dezelfde klik ook de map-click, en die sluit via
                            // closeOnClick de popup die we net openen (zelfde als _parts/preact-map).
                            event.originalEvent.stopPropagation();
                            openStop(stop);
                        }}
                        style={{
                            cursor: "pointer",
                            zIndex: getStopZIndex(stop, stops, isActive),
                        }}
                    >
                        <MapMarker label={stop.firstDay} isActive={isActive} />
                    </Marker>
                );
            })}

            {activeStop && (
                <Popup
                    key={activeStop.id}
                    latitude={activeStop.latitude}
                    longitude={activeStop.longitude}
                    anchor="bottom"
                    offset={[activeStop.revisit * REVISIT_OFFSET, -POPUP_GAP]}
                    closeButton={true}
                    closeOnClick={true}
                    focusAfterOpen={false}
                    onClose={closePoint}
                    className="sb_map-popup"
                >
                    <div>
                        <span class="sb_map-popup_label">
                            {formatDaysLabel(activeStop.days)}
                        </span>

                        {/* Elke dag met zijn eigen titel: van een stop van meerdere dagen weet
                            je anders wel het bereik, maar niet wat je er doet. */}
                        <ul class="sb_map-popup_days">
                            {activeStop.days.map((day) => (
                                <li class="sb_map-popup_day" key={day.id}>
                                    {/* Bij één dag staat het nummer al in de kop erboven. */}
                                    {activeStop.days.length > 1 && (
                                        <span class="sb_map-popup_day_number">
                                            {day.number}
                                        </span>
                                    )}
                                    <span
                                        class="sb_map-popup_day_title"
                                        dangerouslySetInnerHTML={{ __html: day.name }}
                                    />
                                </li>
                            ))}
                        </ul>
                    </div>
                </Popup>
            )}
        </Map>
    );
}

let itineraryMapInitialized = false;

function checkSbPreactMapItinerary() {
    const container = document.querySelector(".sb_preact-map-itinerary");

    if (!container || itineraryMapInitialized) {
        return;
    }

    itineraryMapInitialized = true;
    render(<PreactMapItinerary />, container);
}

const itineraryContainer = document.querySelector(".sb_preact-map-itinerary");

if (itineraryContainer) {
    // Lazy-load the map when it scrolls into view — no button to click.
    if ("IntersectionObserver" in window) {
        const observer = new IntersectionObserver(
            (entries, obs) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        checkSbPreactMapItinerary();
                        obs.disconnect();
                    }
                });
            },
            { rootMargin: "200px" }
        );

        observer.observe(itineraryContainer);
    } else {
        checkSbPreactMapItinerary();
    }
}
